import { Controller } from "@hotwired/stimulus"

// Chip-style tag input. Current tags render as removable chips; typing filters
// the organization's existing tags into a suggestion menu; Enter, comma, or a
// click adds a tag. A hidden field stays in sync (comma-separated) for submit.
export default class extends Controller {
  static targets = [ "hidden", "chips", "input", "menu" ]
  static values = { suggestions: Array }

  connect() {
    this.tags = this.hiddenTarget.value.split(",").map((t) => t.trim()).filter((t) => t.length)
    this.render()
  }

  focus() {
    this.inputTarget.focus()
  }

  keydown(event) {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault()
      this.commitInput()
    } else if (event.key === "Backspace" && this.inputTarget.value === "" && this.tags.length) {
      this.tags.pop()
      this.render()
      this.filter()
    } else if (event.key === "Escape") {
      this.hideMenu()
    }
  }

  filter() {
    const query = this.inputTarget.value.trim().toLowerCase()
    const available = this.suggestionsValue.filter((name) =>
      !this.has(name) && (query === "" || name.toLowerCase().includes(query))
    )
    if (available.length === 0) {
      this.hideMenu()
      return
    }
    this.menuTarget.innerHTML = available.map((name) => `
      <button type="button" data-action="mousedown->tag-input#pick" data-tag="${escapeHtml(name)}"
              class="w-full text-left px-2.5 py-1.5 text-sm text-ink rounded-md hover:bg-panel">
        ${escapeHtml(name)}
      </button>`).join("")
    this.menuTarget.classList.remove("hidden")
  }

  pick(event) {
    event.preventDefault() // keep focus in the input
    this.add(event.currentTarget.dataset.tag)
    this.inputTarget.value = ""
    this.filter()
  }

  remove(event) {
    const name = event.currentTarget.dataset.tag
    this.tags = this.tags.filter((t) => t !== name)
    this.render()
  }

  // Commit whatever is typed when leaving the field, so "type and hit Save"
  // doesn't silently drop the tag.
  blurSoon() {
    this.commitInput()
    setTimeout(() => this.hideMenu(), 150)
  }

  commitInput() {
    this.add(this.inputTarget.value)
    this.inputTarget.value = ""
    this.filter()
  }

  add(name) {
    const trimmed = name.trim().replace(/\s+/g, " ").slice(0, 40)
    if (!trimmed || this.has(trimmed)) return
    this.tags.push(trimmed)
    this.render()
  }

  has(name) {
    return this.tags.some((t) => t.toLowerCase() === name.toLowerCase())
  }

  hideMenu() {
    this.menuTarget.classList.add("hidden")
  }

  render() {
    this.hiddenTarget.value = this.tags.join(", ")
    this.chipsTarget.innerHTML = this.tags.map((t) => `
      <span class="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-md bg-tint text-accent text-xs font-medium">
        ${escapeHtml(t)}
        <button type="button" data-action="tag-input#remove" data-tag="${escapeHtml(t)}"
                class="px-0.5 hover:text-ink" aria-label="Remove ${escapeHtml(t)}">×</button>
      </span>`).join("")
  }
}

function escapeHtml(value) {
  return value.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
}
